import React, { useState, useMemo } from 'react';
import { ShuttleBooking } from '@/hooks/useShuttleBookings';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, MapPin, Users, CreditCard, Phone, Mail } from 'lucide-react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, format, isSameMonth, isSameDay, addMonths, subMonths,
} from 'date-fns';

interface Props {
  bookings: ShuttleBooking[];
}

const ShuttleBookingsCalendar: React.FC<Props> = ({ bookings }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const bookingsByDate = useMemo(() => {
    const map: Record<string, { arrivals: ShuttleBooking[]; returns: ShuttleBooking[] }> = {};
    bookings.forEach((b) => {
      if (b.arrival_date) {
        const key = b.arrival_date;
        if (!map[key]) map[key] = { arrivals: [], returns: [] };
        map[key].arrivals.push(b);
      }
      if (b.return_date) {
        const key = b.return_date;
        if (!map[key]) map[key] = { arrivals: [], returns: [] };
        map[key].returns.push(b);
      }
    });
    return map;
  }, [bookings]);

  const selectedDateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
  const selectedBookings = selectedDateStr ? bookingsByDate[selectedDateStr] : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-bold text-foreground">{format(currentMonth, 'MMMM yyyy')}</h2>
        <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-2">{d}</div>
        ))}
        {calendarDays.map((day) => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const data = bookingsByDate[dateStr];
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isToday = isSameDay(day, new Date());

          return (
            <button
              key={dateStr}
              onClick={() => data && setSelectedDate(day)}
              className={`relative min-h-[70px] p-1 rounded-md border text-left transition-colors
                ${isCurrentMonth ? 'bg-card border-border' : 'bg-muted/30 border-transparent'}
                ${isToday ? 'ring-2 ring-primary' : ''}
                ${data ? 'hover:bg-accent/50 cursor-pointer' : 'cursor-default'}
              `}
            >
              <span className={`text-xs font-medium ${isCurrentMonth ? 'text-foreground' : 'text-muted-foreground/50'}`}>
                {format(day, 'd')}
              </span>
              {data && (
                <div className="mt-1 space-y-0.5">
                  {data.arrivals.length > 0 && (
                    <div className="text-[10px] px-1 py-0.5 rounded bg-primary/20 text-primary font-medium truncate">
                      {data.arrivals.length} arrival{data.arrivals.length > 1 ? 's' : ''}
                    </div>
                  )}
                  {data.returns.length > 0 && (
                    <div className="text-[10px] px-1 py-0.5 rounded bg-green-500/20 text-green-700 font-medium truncate">
                      {data.returns.length} return{data.returns.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedDate} onOpenChange={() => setSelectedDate(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : ''}</DialogTitle>
          </DialogHeader>
          {selectedBookings && (
            <div className="space-y-4">
              {selectedBookings.arrivals.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-primary mb-2">Arrivals ({selectedBookings.arrivals.length})</h3>
                  {selectedBookings.arrivals.map((b) => <BookingCard key={b.id} booking={b} />)}
                </div>
              )}
              {selectedBookings.returns.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-green-700 mb-2">Returns ({selectedBookings.returns.length})</h3>
                  {selectedBookings.returns.map((b) => <BookingCard key={b.id} booking={b} />)}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const BookingCard: React.FC<{ booking: ShuttleBooking }> = ({ booking }) => (
  <div className="p-3 rounded-lg border border-border bg-card mb-2 space-y-1 text-sm">
    <div className="flex justify-between items-center">
      <span className="font-bold text-foreground">{booking.booking_code}</span>
      <Badge variant={booking.booking_status === 'confirmed' ? 'default' : 'secondary'} className="text-[10px]">
        {booking.booking_status}
      </Badge>
    </div>
    <div className="flex items-center gap-1 text-muted-foreground"><MapPin className="w-3 h-3" />{booking.pickup_location} → {booking.dropoff_location}</div>
    <div className="flex items-center gap-1 text-muted-foreground"><Users className="w-3 h-3" />{booking.passengers} pax</div>
    <div className="flex items-center gap-1 text-muted-foreground"><CreditCard className="w-3 h-3" />${booking.total_price.toFixed(2)} ({booking.payment_method})</div>
    <div className="flex items-center gap-1 text-muted-foreground"><Mail className="w-3 h-3" />{booking.full_name} — {booking.email}</div>
    <div className="flex items-center gap-1 text-muted-foreground"><Phone className="w-3 h-3" />{booking.phone}</div>
    {booking.arrival_time && <div className="text-xs text-muted-foreground">Time: {booking.arrival_time}</div>}
    {booking.vehicle_name && <div className="text-xs text-muted-foreground">Vehicle: {booking.vehicle_name}</div>}
  </div>
);

export default ShuttleBookingsCalendar;
